import { Badge, Button, Group, Select, SimpleGrid, Stack, Text, TextInput, Tooltip, useMantineTheme } from "@mantine/core";
import { IconInfoCircle, IconMapPin, IconUserCheck } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CitySelect } from "../components/CitySelect";
import {
  JOB_LIST_SORT_NEWEST,
  JOB_LIST_SORT_OPTIONS,
  POOL_BADGE_TITLE_MANY,
  POOL_BADGE_TITLE_ONE,
  POOL_BADGE_TOOLTIP,
  POOL_MATCHES_TITLE,
} from "../constants/matching";
import {
  estimateFeePaise,
  estimateSharePaise,
  formatCities,
  formatJobExperienceRange,
  formatPaise,
  formatPaiseRange,
} from "../lib/format";
import { renderSegmentOption } from "../components/SegmentOptionLabel";
import { ContentCard, EmptyState, FilterBar, ListScrollSentinel, LoadingOverlay, PageHeader, useInfinitePage } from "../components/ui";
import { useAuth } from "../context/useAuth";
import { jobsApi } from "../lib/api";
import { NAV_COPY, NAV_ICONS } from "../lib/navCopy";
import { maskedCompanyName } from "../lib/kyc";
import { catalogFuzzyFilter } from "../lib/catalogFuzzyFilter";
import { useQueryFilters } from "../lib/useQueryFilters";
import { useSegmentCatalog } from "../lib/useSegmentCatalog";
import { axioTokens } from "../theme";
import type { Job, Segment } from "../types/api";

const JOB_CHIP_STYLE = {
  borderRadius: "var(--axio-radius)",
  background: axioTokens.page,
  border: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
} as const;
const JOB_SORT_SELECT_DATA = [...JOB_LIST_SORT_OPTIONS];
const JOB_WORK_MODE_OPTIONS = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
];
const JOB_FILTER_DEFAULTS = {
  q: "",
  segment: "",
  city: "",
  work_mode: "",
  sort: JOB_LIST_SORT_NEWEST,
};
const JOB_FILTER_ALLOWED = {
  work_mode: JOB_WORK_MODE_OPTIONS.map((item) => item.value),
  sort: JOB_SORT_SELECT_DATA.map((item) => item.value),
};
const JOB_CLEAR_FILTER_KEYS = ["q", "segment", "city", "work_mode"] as const;
const POOL_MATCH_CHIP_STYLE = {
  borderRadius: "var(--axio-radius)",
  background: axioTokens.violetLight,
  border: `${axioTokens.unit[1]}px solid ${axioTokens.violet}`,
  flexShrink: axioTokens.number.zero,
} as const;
const POOL_MATCH_COUNT_STYLE = {
  fontFamily: axioTokens.fontHead,
  fontSize: "var(--axio-font-size-title-sm)",
  letterSpacing: "var(--axio-letter-spacing-tight)",
} as const;
const POOL_MATCH_COPY_GAP = "var(--axio-unit-0)";
const POOL_MATCH_LINE_HEIGHT = "var(--axio-line-height-solid)";
const POOL_MATCH_SUBTEXT_SIZE = "var(--axio-font-size-micro)";
const JOB_TITLE_BLOCK_GAP = "var(--axio-unit-2)";
const JOB_TITLE_LINE_HEIGHT = "var(--axio-line-height-tight)";
const JOB_TITLE_STYLE = {
  fontFamily: axioTokens.fontHead,
  letterSpacing: "var(--axio-letter-spacing-tight)",
} as const;
const JOB_TITLE_STACK_STYLE = {
  flex: "var(--axio-number-one)",
  minWidth: axioTokens.unit[0],
} as const;
const URGENT_LABEL = "Urgent";
const URGENT_TOOLTIP = "The client needs this role filled quickly.";
const YOU_EARN_LABEL = "You earn";
const YOU_EARN_TOOLTIP = "If you source and place. Final amount depends on offered CTC.";
const PLACE_FEE_LABEL = "Place";
const SOURCE_FEE_LABEL = "Source";
const PLATFORM_FEE_LABEL = "Platform";
const PLATFORM_FEE_TOOLTIP = "What AxioHire keeps of the client fee. Place and source go to you.";
const CTC_LABEL = "CTC";

function PoolMatchStat({ count }: { count: number }) {
  const title = count === axioTokens.number.one ? POOL_BADGE_TITLE_ONE : POOL_BADGE_TITLE_MANY;
  return (
    <Tooltip label={POOL_BADGE_TOOLTIP} withArrow multiline>
      <Group gap="var(--axio-unit-6)" align="center" wrap="nowrap" px="sm" py="xs" style={POOL_MATCH_CHIP_STYLE}>
        <Text
          c="violet"
          fw="var(--axio-font-weight-extra-bold)"
          lh={POOL_MATCH_LINE_HEIGHT}
          className="tabular"
          style={POOL_MATCH_COUNT_STYLE}
        >
          {count}
        </Text>
        <Stack gap={POOL_MATCH_COPY_GAP}>
          <Text c="violet" fw="var(--axio-font-weight-semibold)" size="xs" lh={POOL_MATCH_LINE_HEIGHT}>
            {title}
          </Text>
          <Text
            c="violet"
            fz={POOL_MATCH_SUBTEXT_SIZE}
            lh={POOL_MATCH_LINE_HEIGHT}
            opacity={axioTokens.opacity.quiet}
          >
            {POOL_MATCHES_TITLE}
          </Text>
        </Stack>
      </Group>
    </Tooltip>
  );
}

function earnLabel(share: { min: number; max: number } | null): string {
  if (!share) return "—";
  return formatPaiseRange(share.min, share.max);
}

function JobCard({ job, isRecruiter, basePath }: { job: Job; isRecruiter: boolean; basePath: string }) {
  const openingsLeft = job.openings_total - job.openings_filled;
  const fee = estimateFeePaise(job);
  const recruiterShare = estimateSharePaise(fee, job.recruiter_fee_pct);
  const sourcingShare = estimateSharePaise(fee, job.sourcing_fee_pct);
  const platformShare = estimateSharePaise(fee, job.platform_fee_pct);
  const combined =
    recruiterShare || sourcingShare
      ? {
          min: (recruiterShare?.min ?? 0) + (sourcingShare?.min ?? 0),
          max: (recruiterShare?.max ?? 0) + (sourcingShare?.max ?? 0),
        }
      : null;
  const experience = formatJobExperienceRange(job.experience_min, job.experience_max);

  return (
    <Link
      to={`${basePath}/${job.id}`}
      style={{ textDecoration: "none", color: "inherit", display: "block", height: "var(--axio-percentage-full)" }}
    >
      <ContentCard
        interactive
        h="100%"
        padding="xl"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Stack gap="lg" style={{ flex: "var(--axio-number-one)" }}>
          <Group justify="space-between" gap="md" wrap="nowrap" align="flex-start">
            <Stack gap={JOB_TITLE_BLOCK_GAP} style={JOB_TITLE_STACK_STYLE}>
              <Text
                fw="var(--axio-font-weight-bold)"
                size="xl"
                lh={JOB_TITLE_LINE_HEIGHT}
                lineClamp={2}
                style={JOB_TITLE_STYLE}
              >
                {job.title}
              </Text>
              <Text size="sm" c="dimmed" lh={JOB_TITLE_LINE_HEIGHT} lineClamp={1}>
                {maskedCompanyName(job)}
              </Text>
            </Stack>
            {isRecruiter && (job.pool_match_count ?? axioTokens.number.zero) > axioTokens.number.zero ? (
              <PoolMatchStat count={job.pool_match_count ?? axioTokens.number.zero} />
            ) : null}
          </Group>

          <Group gap="sm" wrap="wrap">
            {job.priority === "urgent" ? (
              <Tooltip label={URGENT_TOOLTIP} withArrow>
                <Badge color="red" variant="outline" size="sm">
                  {URGENT_LABEL}
                </Badge>
              </Tooltip>
            ) : null}
            <Tooltip
              label={
                job.is_remote
                  ? "Work location for this role. Remote is allowed."
                  : "Where the candidate will work."
              }
              withArrow
            >
              <Group gap="xs" wrap="nowrap" px="sm" py="xs" style={JOB_CHIP_STYLE}>
                <IconMapPin size="var(--axio-unit-13)" stroke="var(--axio-stroke-width-subtle)" color={axioTokens.muted} />
                <Text size="xs" c="dimmed">
                  {formatCities(job.cities)}
                  {job.is_remote ? " · Remote" : ""}
                </Text>
              </Group>
            </Tooltip>
            {experience && (
              <Tooltip label="Years of experience this role requires." withArrow>
                <Group gap="xs" wrap="nowrap" px="sm" py="xs" style={JOB_CHIP_STYLE}>
                  <IconUserCheck size="var(--axio-unit-13)" stroke="var(--axio-stroke-width-subtle)" color={axioTokens.muted} />
                  <Text size="xs" c="dimmed">
                    {experience}
                  </Text>
                </Group>
              </Tooltip>
            )}
            <Group gap="xs" wrap="nowrap" px="sm" py="xs" style={JOB_CHIP_STYLE}>
              <Text size="xs" c="dimmed">
                {job.guarantee_days} day guarantee
              </Text>
            </Group>
            <Tooltip
              label={`${openingsLeft} of ${job.openings_total} openings still available.`}
              withArrow
            >
              <Group gap="xs" wrap="nowrap" px="sm" py="xs" style={JOB_CHIP_STYLE}>
                <Text size="xs" c="dimmed">
                  {openingsLeft} left
                </Text>
              </Group>
            </Tooltip>
          </Group>

          <div style={{ flex: "var(--axio-number-one)" }} />

          {isRecruiter ? (
            <div className="jobCardEarn">
              <Stack gap="var(--axio-unit-8)">
                <Stack gap="var(--axio-unit-4)">
                  <Group gap="var(--axio-unit-4)" wrap="nowrap">
                    <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                      {YOU_EARN_LABEL}
                    </Text>
                    <Tooltip
                      label={YOU_EARN_TOOLTIP}
                      withArrow
                      multiline
                      w="var(--axio-unit-220)"
                    >
                      <IconInfoCircle size="var(--axio-unit-12)" color={axioTokens.muted} />
                    </Tooltip>
                  </Group>
                  <div className="jobCardEarnAmount" style={{ width: "var(--axio-percentage-full)", whiteSpace: "nowrap" }}>
                    ≈ {earnLabel(combined)}
                  </div>
                </Stack>
                <Group justify="space-between" align="center" wrap="nowrap" gap="md">
                  <Stack gap="var(--axio-unit-2)">
                    <Text size="xs" c="dimmed">
                      {PLACE_FEE_LABEL} {earnLabel(recruiterShare)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {SOURCE_FEE_LABEL} {earnLabel(sourcingShare)}
                    </Text>
                  </Stack>
                  <Stack gap="var(--axio-unit-2)" align="flex-end">
                    <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                      {CTC_LABEL}
                    </Text>
                    <Text size="sm" fw="var(--axio-font-weight-semibold)" className="tabular" style={{ whiteSpace: "nowrap" }}>
                      {formatPaise(job.ctc_min_paise)} – {formatPaise(job.ctc_max_paise)}
                    </Text>
                  </Stack>
                </Group>
                {platformShare ? (
                  <Tooltip label={PLATFORM_FEE_TOOLTIP} withArrow multiline w="var(--axio-unit-220)">
                    <Text size="xs" c="dimmed">
                      {PLATFORM_FEE_LABEL} {earnLabel(platformShare)}
                    </Text>
                  </Tooltip>
                ) : null}
              </Stack>
            </div>
          ) : (
            <div className="jobCardEarn">
              <Group justify="space-between" align="baseline">
                <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                  Fee / hire
                </Text>
                <Text fw="var(--axio-font-weight-bold)" className="moneyAmount" style={{ fontFamily: axioTokens.fontHead }}>
                  {job.fee_basis === "flat"
                    ? formatPaise(job.fee_flat_paise)
                    : `${job.fee_pct_ctc}% of CTC`}
                </Text>
              </Group>
            </div>
          )}
        </Stack>
      </ContentCard>
    </Link>
  );
}

export function JobsPage({
  basePath = "/jobs",
  listJobs,
  showEarnShare,
}: {
  basePath?: string;
  listJobs?: typeof jobsApi.list;
  showEarnShare?: boolean;
}) {
  const { user } = useAuth();
  const theme = useMantineTheme();
  const isRecruiter = showEarnShare ?? user?.role === "recruiter";
  const jobFilters = useQueryFilters(JOB_FILTER_DEFAULTS, {
    debounceKeys: ["q"],
    allowed: JOB_FILTER_ALLOWED,
  });
  const q = jobFilters.draft.q;
  const segment = jobFilters.draft.segment;
  const city = jobFilters.draft.city;
  const workMode = jobFilters.draft.work_mode;
  const sort = jobFilters.draft.sort;
  const appliedQ = jobFilters.values.q;
  const appliedSegment = jobFilters.values.segment;
  const appliedCity = jobFilters.values.city;
  const appliedWorkMode = jobFilters.values.work_mode;
  const appliedSort = jobFilters.values.sort;
  const { flatOptions: segmentOptions } = useSegmentCatalog();
  const hasFilters = Boolean(q || segment || city || workMode);

  const jobCitiesQuery = useQuery({
    queryKey: ["job-available-cities", appliedSegment, appliedWorkMode],
    queryFn: () =>
      jobsApi.availableCities({
        segment: (appliedSegment as Segment) || undefined,
        work_mode: appliedWorkMode || undefined,
      }),
  });
  const jobCities = jobCitiesQuery.data ?? [];

  const jobsQuery = useInfinitePage(
    ["jobs", basePath, appliedQ, appliedSegment, appliedCity, appliedWorkMode, appliedSort],
    (page, pageSize) =>
      (listJobs ?? jobsApi.list)({
        q: appliedQ || undefined,
        segment: (appliedSegment as Segment) || undefined,
        city: appliedCity || undefined,
        work_mode: appliedWorkMode || undefined,
        sort: appliedSort || undefined,
        page,
        page_size: pageSize,
      }),
  );

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.openJobs}
        title={NAV_COPY.common.openJobs}
        subtitle={
          isRecruiter
            ? "Browse openings and submit your best-fit candidates."
            : "Browse open roles and fee splits (read-only)."
        }
      />

      <FilterBar
        actions={
          <Button
            variant="default"
            onClick={() => jobFilters.clear(JOB_CLEAR_FILTER_KEYS)}
            disabled={!hasFilters}
          >
            Clear filters
          </Button>
        }
      >
        <TextInput placeholder="Search title" value={q} onChange={(e) => jobFilters.set({ q: e.currentTarget.value })} w="var(--axio-unit-220)" />
        <Select
          placeholder="All segments"
          data={segmentOptions}
          value={segment || null}
          onChange={(value) => {
            jobFilters.set({ segment: value ?? "", city: "" });
          }}
          clearable
          searchable
          renderOption={renderSegmentOption}
          filter={catalogFuzzyFilter}
          limit={40}
          nothingFoundMessage="No matching segments"
          w="var(--axio-unit-220)"
        />
        <CitySelect
          label=""
          placeholder="All cities"
          flat
          value={city}
          onChange={(value) => jobFilters.set({ city: value })}
          cities={jobCities}
          w="var(--axio-unit-260)"
        />
        <Select
          placeholder="Any work mode"
          data={JOB_WORK_MODE_OPTIONS}
          value={workMode || null}
          onChange={(value) => {
            jobFilters.set({ work_mode: value ?? "", city: "" });
          }}
          clearable
          w="var(--axio-unit-170)"
        />
        {isRecruiter ? (
          <Select
            data={JOB_SORT_SELECT_DATA}
            value={sort}
            onChange={(value) => jobFilters.set({ sort: value ?? JOB_LIST_SORT_NEWEST })}
            w="var(--axio-unit-240)"
          />
        ) : null}
      </FilterBar>

      {jobsQuery.isLoading ? (
        <LoadingOverlay />
      ) : jobsQuery.isError ? (
        <EmptyState
          icon={NAV_ICONS.openJobs}
          title="Unable to load jobs"
          description={
            jobsQuery.error instanceof Error ? jobsQuery.error.message : "Something went wrong."
          }
          action={
            <Button variant="default" onClick={() => void jobsQuery.refetch()}>
              Retry
            </Button>
          }
        />
      ) : jobsQuery.items.length === 0 ? (
        <EmptyState
          icon={NAV_ICONS.openJobs}
          title="No open jobs"
          description="No open jobs match your filters right now."
        />
      ) : (
        <>
          <SimpleGrid type="container" cols={{ base: 1, [theme.breakpoints.sm]: 2 }} spacing="lg">
            {jobsQuery.items.map((job) => (
              <JobCard key={job.id} job={job} isRecruiter={isRecruiter} basePath={basePath} />
            ))}
          </SimpleGrid>
          <ListScrollSentinel
            hasMore={jobsQuery.hasMore}
            loadingMore={jobsQuery.loadingMore}
            onLoadMore={jobsQuery.loadMore}
          />
        </>
      )}
    </Stack>
  );
}
